<template>
  <div class="verification-code">
    <input
      type="text"
      id="verificationCodeInput"
      name="verificationCodeInput"
      v-model="verificationCodeInput"
      :maxlength="inputLength"
      @focus="handleFocus()"
      @blur="toggleFocus(false)"
    />

    <div class="verification-code__digits">
      <span
        v-for="(input, index) in inputLength"
        :key="index"
        class="verification-code__digit"
        :id="focusIndex"
        :class="{
          isFocused: index - 1 === focusIndex && isFocused,
        }"
      >
        <p>{{ verificationCodeInput[input - 1] }}</p>
      </span>
    </div>
    <p v-if="errors" class="error">{{ errors }}</p>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";

const props = defineProps({
  verificationCode: { type: String, default: "" },
  inputLength: {
    type: Number,
    default: parseInt(import.meta.env.VITE_APP_PINCODE_LENGTH),
  },
  errors: { type: String, default: null },
});
const emit = defineEmits(["update:verificationCode"]);
const focusIndex = computed(() => {
  if (props.verificationCode.length === 4) {
    return 2;
  }

  return props.verificationCode.length - 1;
});
const containsAlphabets = /[a-zA-Z]/;
const verificationCodeInput = ref("");
const isFocused = ref(false);

const toggleFocus = (toggle) => {
  isFocused.value = toggle;
};

const handleFocus = () => {
  toggleFocus(true);

  if (verificationCodeInput.value.length >= 0) {
    verificationCodeInput.value = "";
  }
};

watch(
  () => props.verificationCode,
  () => {
    verificationCodeInput.value = props.verificationCode;
  },
  { deep: true }
);

watch(verificationCodeInput, () => {
  // check if digit, slice till 6 chars and return string
  if (verificationCodeInput.value.length > props.inputLength) {
    verificationCodeInput.value = verificationCodeInput.value.slice(0.5);
  }

  // check if string has chars, remove char off and leave digits
  if (containsAlphabets.test(verificationCodeInput.value)) {
    verificationCodeInput.value = verificationCodeInput.value.replace(
      /\D/g,
      ""
    );
  }

  emit("update:verificationCode", verificationCodeInput.value);
});
</script>

<style lang="scss" scoped>
.verification-code {
  margin-bottom: $formFieldSpacing;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  input {
    width: 50%; // for paste bubble to show at right spot
    height: 100%;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    z-index: 2;
    // for ios
    background-color: transparent;
    caret-color: transparent;
    color: transparent;

    @include breakpoint($min-m) {
      width: 100%;
    }
  }

  &__digits {
    display: flex;

    span {
      padding: 2.25rem 1.85rem;
      margin: 0 0.5rem;
      position: relative;
      border-radius: calc($border-radius/2);
      border: 1px solid transparent;
      background: var(--input-background);

      @include breakpoint($min-m) {
        padding: 2.75rem 2.3rem;
        margin: 0 1rem;
      }

      p {
        position: absolute;
        left: 50%;
        top: 50%;
        transform: translate(-50%, -50%);
      }
      &.isFocused {
        border: $border-focus;
      }
    }
  }
  .error {
    position: absolute;
    bottom: 0;
    transform: translateY(110%);
    color: var(--error-color);
    font-size: 1.2rem;
  }
}
</style>
